import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

// import Table from '../../../../common/filterTable/components/Table';
import { AxiosResponse } from 'axios';
import { useQuery } from 'react-query';
import APIs from '../../../../api';
import CardList from '../../../../common/layout/Card/CardList';
import { LoadingDialog } from '../../../../components';
import { DivideLine } from '../../../../components/Layout';
import { EnumOperationType } from '../../../../enum/EnumOperationType';
import { EnumTransactionStatus } from '../../../../enum/EnumTransactionStatus';
import { useAlerting, useTranslation } from '../../../../hooks';
import useLoading from '../../../../hooks/useLoading';
import { useWeb3 } from '../../../../hooks/useWeb3';
import { useAppSelector } from '../../../../reducer/hooks';
import { selectIsMobileView } from '../../../../reducer/mediaSlice';
import { toDisplayTime } from '../../../../utils';
import { tronAddressToEthAddress } from '../../../../utils/tronAddressToEthAddress';
import { MultiSigRawTx, TransactionOperation } from '../types/GetListRes';
import { TableRow } from '../types/Table';
import CurrentSignAddressBar from './components/CurrentSignAddressBar';
import SignOperationCell from './components/SignOperationCell';
import SubHeader from './components/SubHeader';
import useMultiCallSign from './helpers/useMultiCallSign';
import useMultiSigRawTxsTable from './helpers/useMultiSigRawTxsTable';
import useSingleCallSign from './helpers/useSingleCallSign';
import useSwapDetailsExactGetTable from './helpers/useSwapDetailsExactGetTable';
import useSwapDetailsExactPayTable from './helpers/useSwapDetailsExactPayTable';
import useTransactionOperationTableColumns from './helpers/useTransactionOperationTableColumns';
import useTransactionOperationTableRows from './helpers/useTransactionOperationTableRows';
import { ExecutionType } from './types/OperationDetailsRequestBody';

interface IProps {
  selectedRow: TableRow;
  checkStatusRes: number | undefined;
  setCheckStatusRes: Dispatch<SetStateAction<number | undefined>>;
  signatures?: string[];
  setSignatures?: React.Dispatch<React.SetStateAction<string[]>>;
  web3: ReturnType<typeof useWeb3>;
}

const ApproveRejectContent = (props: IProps) => {
  const { selectedRow, signatures, setSignatures, web3, checkStatusRes, setCheckStatusRes } = props;
  const { showLoading, hideLoading } = useLoading();
  const { alerting } = useAlerting();

  const [transactionOperations, setTransactionOperations] = useState<TransactionOperation[]>([]);

  const rowData = selectedRow?.rawData || {};
  const requestId = rowData?.id;

  const handleQuery = async () => {
    if (!requestId) return;
    showLoading('getTransactionOperations');
    const res = await APIs.TransactionRequest.getTransactionOperations({ requestId });
    hideLoading('getTransactionOperations');

    return res;
  };

  const handleQuerySuccess = (data: any) => {
    setTransactionOperations(data || []);
  };

  useQuery(['transactionOperations', requestId], handleQuery, {
    enabled: true,
    onSuccess: handleQuerySuccess,
  });

  useEffect(() => {
    const checkStatus = async () => {
      showLoading('getTransactionOperations');
      const res = await APIs.TransactionRequest.checkStatus({ id: selectedRow?.rawData.id });
      if (!res) return;
      const status = Number(res);
      if (status !== EnumTransactionStatus.Pending) {
        alerting('error', t('shouldNotSigned'));
      }
      hideLoading('getTransactionOperations');
      setCheckStatusRes(status);
    };
    if (rowData.status === EnumTransactionStatus.Pending) {
      checkStatus();
    }
  }, []);

  const multiSigRawTxs = JSON.parse(selectedRow?.rawData?.multiSigRawTxs || '[]') || [];
  // const transactionOperations = selectedRow.rawData?.transactionOperations || [];
  const allowedSignerAddresses = multiSigRawTxs?.[0]?.multi_sig_raw_tx.signer_addresses || [];

  // const { account, connect, isConnected, ensureWalletConnected, signTypedDataWithWrappedPayload } =
  //   useWeb3({
  //     allowedSignerAddresses: allowedSignerAddresses,
  //   });

  const { isConnected, ensureWalletConnected, signTypedDataWithWrappedPayload } = web3;

  const { t, translate } = useTranslation('transactionRequest');

  const { signSingleCallTypedData } = useSingleCallSign({ signTypedDataWithWrappedPayload });
  const { signMultiCallTypedData } = useMultiCallSign({ signTypedDataWithWrappedPayload });

  // Operations
  const { rows } = useTransactionOperationTableRows({
    apiList: transactionOperations,
  });

  const columns = useTransactionOperationTableColumns();

  // Swap Details
  const { exactGetColumns, exactGetRows } = useSwapDetailsExactGetTable({ transactionOperations });
  const { exactPayColumns, exactPayRows } = useSwapDetailsExactPayTable({ transactionOperations });

  const isUniSwap = selectedRow.rawData?.operationType === EnumOperationType.Uniswap;
  const isExactGet =
    JSON.parse(transactionOperations?.[0]?.executionDetail || '{}')?.execution_type ===
    ExecutionType.ExactGet;

  const swapDetailsColumns = isExactGet ? exactGetColumns : exactPayColumns;
  const swapDetailsRows = isExactGet ? exactGetRows : exactPayRows;

  // Multi Sig Raw Txs
  const onSignConfirm = async ({
    rowData,
    rawTx,
    selectedRawTxIndex,
  }: {
    rowData: any;
    rawTx: any;
    selectedRawTxIndex: number;
  }) => {
    let signature = '';
    const multiSigRawTx = rawTx?.multi_sig_raw_tx;
    const chainType = rowData?.chainType;
    const chainId = multiSigRawTx?.chain_id;
    const isTron = Number(chainType) === 3;

    console.log('onSignConfirm isTron ', {
      isTron,
      chainType,
      rowData,
    });

    const fromAddress = isTron
      ? tronAddressToEthAddress(multiSigRawTx?.from || '')
      : multiSigRawTx?.from;

    const isMultiCall = String(rawTx?.hash_prefix).toLowerCase() === '0xff';

    console.log('[Sign] Row Data ', {
      rowData,
      rawTx,
      isMultiCall,
    });

    try {
      await ensureWalletConnected();

      const signFunc = isMultiCall ? signMultiCallTypedData : signSingleCallTypedData;

      signature = await signFunc({
        rowData,
        rawTx: multiSigRawTx,
        isTron,
        chainId,
        fromAddress,
      });

      if (signature && setSignatures) {
        const newSignatures = [...(signatures || [])];
        newSignatures[selectedRawTxIndex] = signature;
        setSignatures(newSignatures);
      }
    } catch (error: any) {
      // Custom error msg from web3 hook
      if (typeof error === 'string') {
        return alerting('error', error);
      }

      // User rejection (cancel)
      if (error?.code === 4001) {
        return;
      }

      console.error('error signing payload ', error);
      alerting('error', `${error?.message} (${error?.code})`);
    } finally {
      return signature;
    }
  };

  const getSignButtonDisabled = () => {
    //checkStatusRes will always return undefined if Status is not Pending
    if (!checkStatusRes) return true;
    return Number(checkStatusRes) !== EnumTransactionStatus.Pending;
  };

  const renderMultiSigRawTxsOperationCell = (params: any) => {
    const selectedRawTxIndex = multiSigRawTxs.findIndex((item: MultiSigRawTx) => {
      return item.multi_sig_raw_tx?.tx_hash === params.row?.rawData?.multi_sig_raw_tx?.tx_hash;
    });

    const onOperationClick = () => {
      onSignConfirm({
        rowData: selectedRow?.rawData,
        rawTx: params.row?.rawData,
        selectedRawTxIndex: selectedRawTxIndex,
      });
    };

    return (
      <SignOperationCell
        selectedRawTxIndex={selectedRawTxIndex}
        signatures={signatures}
        setSignatures={setSignatures || (() => {})}
        onOperationClick={onOperationClick}
        isWalletConnected={isConnected}
        isButtonDisabled={getSignButtonDisabled()}
      />
    );
  };

  const { multiSigRawTxsColumns, multiSigRawTxsRows } = useMultiSigRawTxsTable({
    selectedRow,
    multiSigRawTxs,
    renderMultiSigRawTxsOperationCell,
  });

  return (
    <>
      {isUniSwap && (
        <>
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <h3>{t('swapDetails')}</h3>
            <CardList rows={swapDetailsRows} columns={swapDetailsColumns} />
            <DivideLine verticalMargin="32px" />
          </div>
        </>
      )}
      <div>
        <h3>{t('operations')}</h3>
        <SubHeader
          title={t('batchCreationTime')}
          content={toDisplayTime(selectedRow?.rawData?.createdDate)}
        />
        <CardList rows={rows} columns={columns} />
        <DivideLine verticalMargin="32px" />
      </div>

      <div>
        <h3>{t('multiSigRawTxs')}</h3>
        {/* <SubHeader title={t('currentSignAddress')} content={account || ''} contentOnNewLine /> */}
        <CurrentSignAddressBar multiSigRawTxs={multiSigRawTxs} web3={web3} />
        <CardList rows={multiSigRawTxsRows} columns={multiSigRawTxsColumns} />
      </div>
    </>
  );
};

export default ApproveRejectContent;
